/**
 * Crunch Design System — Button & IconButton
 *
 * Figma components:
 *   button      (key: a42b3b6e9de871ccfa707edb4afdd7a2331756e9)
 *   icon-button (key: e5aa904997ae8702a69b44204e1ce930bf2c52ec)
 *
 * Usage:
 *   import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
 *   import { faPlus, faPencil } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';
 *
 *   <Button type="primary" size="default" leadingIcon={<FontAwesomeIcon icon={faPlus} />}>Save</Button>
 *   <IconButton type="floating" size="small" icon={<FontAwesomeIcon icon={faPencil} />} label="Edit" />
 */
import * as React from 'react';

export function Button({
    type = "secondary",
    size = "default",
    disabled = false,
    leadingIcon,
    trailingIcon,
    children,
    className,
    ref,
    ...rest
}: {
    type?: "primary" | "secondary" | "floating" | "important" | "info" | "success" | "warning" | "danger";
    size?: "default" | "small";
    disabled?: boolean;
    leadingIcon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    children?: React.ReactNode;
    className?: string;
    ref?: React.Ref<HTMLButtonElement>;
    [key: string]: any;
}) {
    const cls = [
        "cr-btn",
        `cr-btn--${type}`,
        size !== "default" && `cr-btn--${size}`,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <button className={cls} disabled={disabled} ref={ref} {...rest}>
            {leadingIcon && <span className="cr-btn__icon">{leadingIcon}</span>}
            <span className="cr-btn__label">{children}</span>
            {trailingIcon && <span className="cr-btn__icon">{trailingIcon}</span>}
        </button>
    );
}

export function IconButton({
    type = "secondary",
    size = "default",
    disabled = false,
    icon,
    label,
    className,
    ref,
    ...rest
}: {
    type?: "primary" | "secondary" | "floating" | "important" | "info" | "success" | "warning" | "danger";
    size?: "default" | "small";
    disabled?: boolean;
    icon?: React.ReactNode;
    label: string;
    className?: string;
    ref?: React.Ref<HTMLButtonElement>;
    [key: string]: any;
}) {
    const cls = [
        "cr-btn-icon",
        `cr-btn-icon--${type}`,
        size !== "default" && `cr-btn-icon--${size}`,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <button className={cls} disabled={disabled} aria-label={label} ref={ref} {...rest}>
            {icon}
        </button>
    );
}
